import { SelectSearchOption } from 'react-select-search';
import { getHightestNumberInCollection } from '../../../../infrastructure/helpers/common/collectionOperationHelper';
import copyArray from '../../../../infrastructure/helpers/common/copyArray';
import sortingHelper from '../../../../infrastructure/helpers/common/sortingHelper';
import stringHelper from '../../../../infrastructure/helpers/common/stringHelper';
import operandHelper from '../../../../infrastructure/helpers/functions/operand/operandHelper';
import SelectOption from '../../../../infrastructure/types/SelectOption';
import Operand from '../../../../types/functions/Operand/Operand';
import GlobalModuleConfig from '../../../../types/report/GlobalModuleConfig';
import IdNameAndNumber from '../../../../types/report/IdNameAndNumber';
import Mode from '../../../../types/report/Mode';
import ReportConfig from '../../../../types/report/ReportConfig';
import UserDefinedFunction from '../../../../types/report/UserDefinedFunction';
import { getByAssignmentReferencesInGlobalConfig } from '../../GlobalModule/GlobalModule.Helpers';
import Assignment from '../../../../types/functions/Assignment/Assignment';
import Condition from '../../../../types/functions/Condition/Condition';
import reportConfigNavigation from '../../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import { htmlIds } from '../../../../infrastructure/helpers/report/navigation/reportConfigNavigation.HtmlIds';
import operandType from '../../../../infrastructure/constants/functions/operandType';

const createFunctionOption = (func: UserDefinedFunction): SelectOption => {
    return {
        value: func.number.toString(),
        text: stringHelper.isNonEmpty(func.name) ? func.name : `Function ${func.number}`
    };
};

const prepareOptionsForGlobalUdfs = (globalModuleFunctions: UserDefinedFunction[], currentConfigFunctions: UserDefinedFunction[]): SelectSearchOption[] => {

    let options: SelectSearchOption[] = [];

    let configFunctionsIds = currentConfigFunctions.filter(x => x.mode === Mode.Global).map(x => x.globalUDFModuleId);

    globalModuleFunctions.filter(f => !configFunctionsIds.includes(f.id as number)).forEach((udf) => {

        options.push({
            name: udf.name,
            value: udf?.id ? udf.id.toString() : '',
            items: [{ name: udf.name, value: udf?.id ? udf.id.toString() : '' }]
        });
    });

    return options;
}

const getOldAndNewFunctionNumbers = (reportConfigGlobalFunctions: UserDefinedFunction[], globalModuleFunctions: UserDefinedFunction[]): Map<number, number> => {

    let oldAndNewFunctionsNumbers = new Map<number, number>();

    reportConfigGlobalFunctions.forEach((f) => {
        let globalFunction = globalModuleFunctions.find(x => x.id === f.globalUDFModuleId);

        oldAndNewFunctionsNumbers.set(globalFunction?.number as number, f.number);
    });

    return oldAndNewFunctionsNumbers;
}

const updateFunctionInCondition = (reportConfigGlobalFunctions: UserDefinedFunction[], globalModuleFunctions: UserDefinedFunction[], condition: Condition): void => {

    let oldAndNewFunctionsNumbers = getOldAndNewFunctionNumbers(reportConfigGlobalFunctions, globalModuleFunctions);

    if (oldAndNewFunctionsNumbers?.size) {

        updateConditionFunctionNumber(condition, oldAndNewFunctionsNumbers);
    }
}

const updateFunctionInAssignment = (reportConfigGlobalFunctions: UserDefinedFunction[], globalModuleFunctions: UserDefinedFunction[], assignment: Assignment): void => {

    let oldAndNewFunctionsNumbers = getOldAndNewFunctionNumbers(reportConfigGlobalFunctions, globalModuleFunctions);

    if (oldAndNewFunctionsNumbers?.size) {

        updateAssignmentFunctionNumber(assignment, oldAndNewFunctionsNumbers);
    }
}

const navigationToParameter = (functionNumber: number, parameterNumber: number) => {
    let functionId = reportConfigNavigation.buildFunctionId(functionNumber);

    reportConfigNavigation.navigateToItemInSection(functionId, htmlIds.userDefinedFunctions)

    let parameterId = reportConfigNavigation.buildFunctionParameterId(parameterNumber);
    reportConfigNavigation.navigateToItem(parameterId);
}

const findReferencedComponentsInFunctions = (globalModuleConfig: GlobalModuleConfig,
    reportConfig: ReportConfig,
    globalFunctionModuleId: number,
    referencedUdfs: UserDefinedFunction[]): void => {

    let udf = globalModuleConfig?.userDefinedFunctions?.find(x => x.id === globalFunctionModuleId) as UserDefinedFunction;

    getByAssignmentReferencesInGlobalConfig(globalModuleConfig, udf.assignment, [], referencedUdfs, new Set(), new Set(), new Set());
}

const prepareMissingFunctions = (maxNumberInFunctions: number, oldAndNewNumbers: Map<number, number>, missingFunctions: UserDefinedFunction[]): UserDefinedFunction[] => {

    let missingFunctionsCopy = copyArray(missingFunctions);
    sortingHelper.sortByNumberAsc(missingFunctionsCopy as IdNameAndNumber[]);

    missingFunctionsCopy.forEach((func) => {
        maxNumberInFunctions++;

        func.globalUDFModuleId = func.id as number;

        func.id = null;

        func.errors = [];

        func.mode = Mode.Global;

        oldAndNewNumbers.set(func.number, maxNumberInFunctions);

        func.number = maxNumberInFunctions;

        func.parameters.forEach(p => p.errors = []);
    });

    return missingFunctionsCopy;
}

const updateFunctionsConditions = (functions: UserDefinedFunction[], oldAndNewNumbers: Map<number, number>): UserDefinedFunction[] => {
    functions.forEach((func) => {
        operandHelper.proceedWhitAssignment(func.assignment, (operand: Operand) => {
            if (operand.functionNumber as number > 0 && oldAndNewNumbers.has(operand.functionNumber as number)) {

                operand.functionNumber = oldAndNewNumbers.get(operand.functionNumber as number) as number;
            }
        })
    });

    return functions;
}

const prepareFunctions = (missingFunctions: UserDefinedFunction[], reportConfigFunctions: UserDefinedFunction[], functionsOldAndNewNumbers?: Map<number, number>): UserDefinedFunction[] => {

    let configFunctions = copyArray(reportConfigFunctions);

    let maxNumberInFunctions = getHightestNumberInCollection(configFunctions);

    if (!functionsOldAndNewNumbers) {
        functionsOldAndNewNumbers = new Map<number, number>();
    }

    let functions = prepareMissingFunctions(maxNumberInFunctions, functionsOldAndNewNumbers, missingFunctions)

    let allFunctions = configFunctions.concat(functions);

    updateFunctionsConditions(allFunctions, functionsOldAndNewNumbers);

    return allFunctions;
}

const normalizeFunctions = (functions: UserDefinedFunction[], number: number): Map<number, number> => {

    let oldAndNewFunctionNumbers = new Map<number, number>();

    functions.forEach((f) => {
        number++;
        oldAndNewFunctionNumbers.set(f.number, number);
        f.number = number;
    });

    return oldAndNewFunctionNumbers;
}

const prepareWarningMessageOnGlobalFunctionAdd = (referencedFunctions: UserDefinedFunction[]): string => {

    let missingFunctions = referencedFunctions?.map(x => `"${x.name}"`)?.join(', ');

    return missingFunctions ? `Please add referenced Functions ${missingFunctions}.` : '';
}

const updateAssignmentFunctionNumber = (assignment: Assignment, oldAndNewFunctionNumbers: Map<number, number>) => {
    operandHelper.proceedWhitAssignment(assignment, (o: Operand) => {
        if (o.functionNumber) {
            o.functionNumber = oldAndNewFunctionNumbers.get(o.functionNumber) || o.functionNumber;
        }
    });
}

const updateConditionFunctionNumber = (condition: Condition, oldAndNewFunctionNumbers: Map<number, number>) => {
    operandHelper.proceedWhitCondition(condition, (o: Operand) => {
        if (o.functionNumber) {
            o.functionNumber = oldAndNewFunctionNumbers.get(o.functionNumber) || o.functionNumber;
        }
    });
}

const updateReportComponentToFunctionCondition = (condition: Condition, functions: UserDefinedFunction[]): void => {

    operandHelper.proceedWhitCondition(condition, (operand: Operand) => {

        if (operand.functionNumber) {

            let func = functions.find(x => x.number === operand.functionNumber);
            operand.functionNumber = func?.number as number;
            operand.operandType = operandType.globalUserDefinedFunction;
        }
    });
}

const updateReportComponentToFunctionAssignment = (assignment: Assignment, functions: UserDefinedFunction[]): void => {

    operandHelper.proceedWhitAssignment(assignment, (operand: Operand) => {

        if (operand.functionNumber) {

            let func = functions.find(x => x.number === operand.functionNumber);
            operand.functionNumber = func?.number as number;
            operand.operandType = operandType.globalUserDefinedFunction;
        }
    });
}

export {
    createFunctionOption,
    prepareFunctions,
    normalizeFunctions,
    findReferencedComponentsInFunctions,
    prepareWarningMessageOnGlobalFunctionAdd,
    prepareOptionsForGlobalUdfs,
    updateFunctionInAssignment,
    updateFunctionInCondition,
    navigationToParameter,
    updateConditionFunctionNumber,
    updateAssignmentFunctionNumber,
    updateReportComponentToFunctionCondition,
    updateReportComponentToFunctionAssignment,
    updateFunctionsConditions
};
